import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { motion } from 'framer-motion';

import { 
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import ReorderSystem from '../components/ReorderSystem';
import LoadingPage from '../components/Loader';
import { showSuccessNotification, showErrorNotification, showInfoNotification, showWarningNotification } from '../components/NotificationManager';

const ReorderPage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const byteUser = localStorage.getItem('byteUser');
    
    if (!token || !byteUser) {
      showErrorNotification('Please log in to access reorder functionality');
      navigate('/login');
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      const userData = JSON.parse(byteUser);
      
      // Check if token is expired
      if (decodedToken.exp * 1000 < Date.now()) {
        showErrorNotification('Session expired. Please log in again.');
        localStorage.removeItem('token');
        localStorage.removeItem('byteUser');
        navigate('/login');
        return;
      }

      setUser(userData);
    } catch (error) {
      console.error('Error decoding token:', error);
      showErrorNotification('Invalid session. Please log in again.');
      navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const handleOrderSuccess = (newOrder) => {
    showSuccessNotification(
      <div className="flex items-center gap-2">
        <CheckCircleIcon className="h-5 w-5 text-green-500" />
        <span>Order placed successfully!</span>
      </div>,
      { duration: 4000 }
    );

    // Optional: Navigate to order tracking or history
    setTimeout(() => {
      navigate('/user/order-history');
    }, 2000);
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">Please log in to access the reorder functionality</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-cheese text-crust px-6 py-2 rounded-lg font-medium hover:bg-yellow-500"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50 pt-16 pb-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate('/user/dashboard')}
              className="p-2 rounded-lg hover:bg-white/50 transition-colors"
            >
              <ArrowLeftIcon className="h-6 w-6 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Reorder System</h1>
              <p className="text-gray-600">Welcome back, {user.username}!</p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-cheese/20 to-orange-200/20 p-4 rounded-xl border border-cheese/30">
            <h3 className="font-semibold text-gray-900 mb-2">How Reordering Works</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
              <div>
                <h4 className="font-medium text-red-600 mb-1">Quick Reorder</h4>
                <p>Instantly reorder your saved favorite meals with one click. Perfect for your regular orders!</p>
              </div>
              <div>
                <h4 className="font-medium text-primary-600 mb-1">Regular Reorder</h4>
                <p>Create a new order based on any previous order. You can modify delivery details as needed.</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Reorder System Component */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <ReorderSystem 
            userToken={localStorage.getItem('token')}
            onOrderSuccess={handleOrderSuccess}
          />
        </motion.div>

        {/* Additional Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h3 className="font-bold text-gray-900 mb-3">💡 Pro Tips</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Save your most frequent orders as Quick Reorders</li>
              <li>• Use Regular Reorder to modify past orders</li>
              <li>• Update delivery details before reordering</li>
              <li>• Check your wallet balance before placing orders</li>
            </ul>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h3 className="font-bold text-gray-900 mb-3">🔄 Need Help?</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <p>• Find Order IDs in your order history</p>
              <p>• Quick Reorders are saved automatically</p>
              <p>• All orders verify meal availability</p>
              <div className="mt-3">
                <button
                  onClick={() => navigate('/user/order-history')}
                  className="text-cheese hover:text-yellow-600 font-medium"
                >
                  View Order History →
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ReorderPage;
